import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { urlFotoFicha } from '@/lib/foto';
import { mascaraCPF, mascaraTelefone } from '../utils';
import type { PessoaSalva } from '../types';

interface CartaoConferenciaProps {
  pessoa: PessoaSalva;
  onEditar: () => void;
  // Quando presente, vira um link (ex.: para /meu-perfil) em vez de edição inline.
  linkEditar?: string;
}

// Mostra os dados já salvos de uma pessoa (a própria ou alguém já cadastrado),
// com foto e endereço, para que ela apenas confira antes de seguir.
export default function CartaoConferencia({
  pessoa,
  onEditar,
  linkEditar,
}: CartaoConferenciaProps) {
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    setFotoUrl(null);
    if (pessoa.foto_url) {
      urlFotoFicha(pessoa.foto_url).then((url) => {
        if (ativo) setFotoUrl(url);
      });
    }
    return () => {
      ativo = false;
    };
  }, [pessoa.foto_url]);

  const endereco = [
    pessoa.endereco,
    pessoa.complemento,
    pessoa.bairro,
    pessoa.cidade && pessoa.uf ? `${pessoa.cidade}/${pessoa.uf}` : pessoa.cidade,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className="rounded-lg border border-secondary-200 bg-white p-6">
      <div className="flex items-start gap-4">
        {fotoUrl ? (
          <img
            src={fotoUrl}
            alt={pessoa.nome}
            className="h-16 w-16 shrink-0 rounded-full object-contain object-center"
          />
        ) : (
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-400">
            <i className="ri-user-line text-3xl" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-lg font-medium text-foreground-950">{pessoa.nome}</p>
          <p className="mt-1 text-sm text-secondary-600">
            {pessoa.cpf ? `CPF ${mascaraCPF(pessoa.cpf)}` : ''}
            {pessoa.cpf && pessoa.telefone ? ' · ' : ''}
            {pessoa.telefone ? mascaraTelefone(pessoa.telefone) : ''}
          </p>
          {endereco && <p className="mt-1 text-sm text-secondary-600">{endereco}</p>}
        </div>
      </div>

      {linkEditar ? (
        <Link
          to={linkEditar}
          className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-user-line" />
          </span>
          Editar meus dados
        </Link>
      ) : (
        <button
          type="button"
          onClick={onEditar}
          className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          Editar dados
        </button>
      )}
    </div>
  );
}